import { useState } from "react";
import { X, Loader2, ShieldCheck, Phone } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";

export default function BookConsult({ open, onClose }) {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", company: "",
    matter_brief: "", preferred_time: "", urgency_reason: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!open) return null;

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.matter_brief.trim().length < 10) {
      setErr("Please describe your matter briefly (at least 10 characters).");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/consults/checkout", {
        package_id: "urgent_30min",
        origin_url: window.location.origin,
        ...form,
      });
      window.location.href = r.data.url;
    } catch (e2) {
      setErr(formatErr(e2));
      setBusy(false);
      toast.error("Could not start checkout — try again.");
    }
  };

  return (
    <>
      <div className="drawer-bg" onClick={onClose} data-testid="consult-modal-bg" />
      <div className="consult-modal" data-testid="consult-modal" role="dialog" aria-modal="true">
        <button className="consult-close" onClick={onClose} data-testid="consult-close-btn"><X size={16} /></button>

        <div className="intake-eyebrow" style={{ marginBottom: 6 }}>Urgent · Paid consultation</div>
        <h2 className="serif" style={{ fontSize: 26, fontWeight: 600, margin: "0 0 6px", letterSpacing: "-0.02em" }}>
          Book a 15-minute call.
        </h2>
        <p style={{ fontSize: 13.5, color: "var(--muted)", margin: "0 0 18px" }}>
          A focused 15-minute consultation with Reza Chawla. <strong style={{ color: "var(--copper-deep)" }}>$95 flat.</strong> Same-day availability for time-sensitive matters.
        </p>

        <form onSubmit={submit}>
          <div className="frow"><label>Full name *</label>
            <input data-testid="book-consult-name" required value={form.full_name} onChange={set("full_name")} placeholder="Your full name" />
          </div>
          <div className="frow"><label>Email *</label>
            <input data-testid="book-consult-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" />
          </div>
          <div className="frow"><label>Phone *</label>
            <input data-testid="book-consult-phone" type="tel" required value={form.phone} onChange={set("phone")} placeholder="(___) ___-____" />
          </div>
          <div className="frow"><label>Company</label>
            <input data-testid="book-consult-company" value={form.company} onChange={set("company")} placeholder="Optional" />
          </div>
          <div className="frow"><label>Brief description of the matter *</label>
            <textarea data-testid="book-consult-matter" required value={form.matter_brief} onChange={set("matter_brief")} rows={4} placeholder="What happened, who is involved, any deadlines you know of." style={{ resize: "vertical", lineHeight: 1.6, padding: "10px 12px", border: "1px solid var(--rule)", fontFamily: "var(--body)", fontSize: 13.5 }} />
          </div>
          <div className="frow"><label>Preferred time window</label>
            <input data-testid="book-consult-preferred-time" value={form.preferred_time} onChange={set("preferred_time")} placeholder="e.g., today after 3pm CT, or tomorrow morning" />
          </div>
          <div className="frow"><label>Why is this urgent?</label>
            <input data-testid="book-consult-urgency" value={form.urgency_reason} onChange={set("urgency_reason")} placeholder="e.g., TRO needed, statute deadline within 30 days" />
          </div>

          {err && <div data-testid="consult-error" style={{ color: "#b73b2a", fontSize: 12, fontFamily: "var(--mono)", marginBottom: 10 }}>{err}</div>}

          <button className="btn-copper" type="submit" disabled={busy} style={{ width: "100%" }} data-testid="book-consult-submit">
            {busy ? <><Loader2 size={14} className="animate-spin" /> Redirecting to secure checkout...</> : <>Continue to secure checkout · $95</>}
          </button>

          <div className="pay-methods" data-testid="pay-methods">
            <span className="pay-methods-label">Pay how you want</span>
            <span className="pay-pill">Card</span>
            <span className="pay-pill bnpl">Affirm</span>
            <span className="pay-pill bnpl">Klarna</span>
            <span className="pay-pill bnpl">Afterpay</span>
            <div className="pay-bnpl-note">Split into 4 interest-free payments or finance over 3–12 months — choose at checkout. Subject to lender approval.</div>
          </div>

          <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center", fontSize: 11, color: "var(--muted)" }}>
            <ShieldCheck size={13} style={{ color: "var(--copper)" }} />
            Secure payment by Stripe. The fee is a flat consultation deposit and is non-refundable once the call is held.
          </div>

          <div style={{ marginTop: 10, fontSize: 11, color: "var(--muted)", display: "flex", alignItems: "center", gap: 6 }}>
            <Phone size={11} /> Or call directly: <a href="tel:+17132912875" style={{ color: "var(--copper)", textDecoration: "none", fontWeight: 600 }}>(713) 291-2875</a>
          </div>
        </form>
      </div>
    </>
  );
}
